import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Check, Copy, Lock, RefreshCw, Sparkles, TrendingUp, X, Zap } from "lucide-react";

import { TREND_REPORT_TYPE } from "../api/trends";
import { api } from "../utils/apiClient";
import { getUserFromToken } from "../utils/jwtUtils";
import AiOptimizationGuide from "./AiOptimizationGuide";
import CrossReportBanner from "./CrossReportBanner";
import DetailedAnalysisView from "./DetailedAnalysisView";
import DownloadPdfButton from "./DownloadPdfButton";
import ImplementationGuideModal from "./ImplementationGuideModal";
import LoadingSpinner from "./LoadingSpinner";
import OverviewView from "./OverviewView";
import RecommendationsView from "./RecommendationsView";
import TailorRequirementsView from "./TailorRequirementsView";
import UpdatesView from "./UpdatesView";

const NAV_HEIGHT_NORMAL = 80; // Approximate navbar height when not scrolled
const NAV_HEIGHT_SCROLLED = 60; // Approximate navbar height when scrolled

const buildRecommendationState = (suggestions = []) =>
  suggestions.map((suggestion, index) => ({
    id: index,
    ...suggestion,
    completed: false,
  }));

const normalizeAuditResponse = (auditResponse) => {
  if (!auditResponse?.data) {
    return {
      accessLevel: null,
      reportData: null,
      technicalAnalysisData: null,
    };
  }

  if (auditResponse.access_level === "full") {
    return {
      accessLevel: "full",
      reportData: auditResponse.data,
      technicalAnalysisData: auditResponse.technical_analysis_data || null,
    };
  }

  return {
    accessLevel: auditResponse.access_level || "summary",
    reportData: {
      quality_score: auditResponse.data.overall_score,
      overall_score_band: auditResponse.data.overall_score_band,
      category_scores: auditResponse.data.category_scores,
      score_explanations: auditResponse.data.score_explanations,
      score_methodology: auditResponse.data.score_methodology,
      url: auditResponse.data.url,
    },
    technicalAnalysisData: {
      technical_score: auditResponse.data.technical_score,
      technical_score_band: auditResponse.data.technical_score_band,
    },
  };
};

const AutoFixModal = ({ taskId, recommendation, onConfirmFix, onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [fixContent, setFixContent] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (recommendation && recommendation.id !== null) {
      setIsLoading(true);
      setFixContent(null);

      const fetchAutoFix = async () => {
        try {
          const response = await api.get(
            `/audit/${taskId}/recommendation/${recommendation.id}/autofix`,
          );
          setFixContent(response.data);
        } catch (error) {
          setFixContent({
            title: "Error Generating Fix",
            content: `An error occurred while generating the fix: ${error.message}`,
          });
        } finally {
          setIsLoading(false);
        }
      };

      fetchAutoFix();
    }
  }, [recommendation, taskId]);

  if (!recommendation) return null;

  const handleCopy = () => {
    if (fixContent?.content) {
      // This is a simplified copy for demonstration.
      // In a real app, you'd parse the markdown/JSON.
      const textToCopy = fixContent.content
        .replace(/```json\n|```/g, "")
        .replace(/<script...>/, "")
        .replace("</script>", "");
      navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="rounded-3xl shadow-cloud w-full max-w-3xl max-h-[90vh] flex flex-col bg-surface-container-lowest">
        <header className="p-6 border-b border-surface-container-high flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-primary" />
            <h3 className="text-xl font-bold text-on-surface">AI-Powered Auto-Fix</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </header>

        <main className="p-8 overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <LoadingSpinner size="md" />
              <p className="mt-4 font-semibold text-on-surface-variant">Generating AI fix</p>
              <p className="mt-4 text-on-surface-variant">{recommendation.description}</p>
            </div>
          ) : (
            <div>
              <h4 className="text-lg font-semibold mb-2 text-on-surface">
                {fixContent?.title || "Auto-Fix Solution"}
              </h4>
              <p className="text-sm mb-4 text-on-surface-variant">
                AI has generated the following solution. Review and deploy to mark this
                recommendation as complete.
              </p>
              <div className="relative bg-inverse-surface text-inverse-on-surface p-4 rounded-lg">
                <button
                  onClick={handleCopy}
                  className="absolute top-2 right-2 bg-surface-container-high hover:bg-surface-container-low text-on-surface px-3 py-1 rounded-md text-xs font-semibold flex items-center gap-1.5"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                  {copied ? "Copied!" : "Copy"}
                </button>
                <pre className="whitespace-pre-wrap text-sm overflow-x-auto">
                  <code>
                    {fixContent?.content?.replace(/```json\n|```/g, "") || "No content available"}
                  </code>
                </pre>
              </div>
            </div>
          )}
        </main>

        <footer className="p-6 border-t border-surface-container-high bg-surface-container-low flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-semibold text-on-surface bg-surface-container-low border border-surface-container-high hover:bg-surface-container-high rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirmFix(recommendation.id)}
            disabled={isLoading}
            className="px-6 py-2 text-sm font-semibold text-on-primary bg-gradient-to-br from-primary to-primary-container rounded-xl hover:opacity-90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <Zap className="w-4 h-4" />
            Deploy Fix & Mark Complete
          </button>
        </footer>
      </div>
    </div>
  );
};

const Report = ({
  data: propData,
  auditId: propAuditId,
  technicalAnalysisData: propTechnicalAnalysisData,
  onNewAudit,
}) => {
  const navigate = useNavigate();
  const { audit_id: urlAuditId } = useParams();
  const location = useLocation();

  // Get data from navigation state if available (when coming from AuditEngine)
  const navigationState = location.state;
  const navigationAuditResponse = navigationState?.auditResponse;
  const navigationAuditId = navigationAuditResponse?.audit_id || navigationState?.auditId;

  const propAuditResponse = useMemo(
    () =>
      propData
        ? {
            status: "completed",
            access_level: propTechnicalAnalysisData ? "full" : "summary",
            data: propData,
            technical_analysis_data: propTechnicalAnalysisData,
            audit_id: propAuditId,
          }
        : null,
    [propData, propTechnicalAnalysisData, propAuditId],
  );

  // Use props if provided, otherwise navigation state, otherwise URL parameter

  const auditId = propAuditId || navigationAuditId || urlAuditId;

  const [activeTab, setActiveTab] = useState("Overview");

  const [fetchedAuditResponse, setFetchedAuditResponse] = useState(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const auditResponse =
    propAuditResponse || fetchedAuditResponse || navigationAuditResponse || null;
  const normalizedAuditResponse = useMemo(
    () => normalizeAuditResponse(auditResponse),
    [auditResponse],
  );
  const { accessLevel, reportData: data, technicalAnalysisData } = normalizedAuditResponse;
  const hasFullAuditAccess = accessLevel === "full";

  const [recommendations, setRecommendations] = useState(() =>
    buildRecommendationState(data?.suggestions || []),
  );
  const [viewingGuide, setViewingGuide] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [fixingRec, setFixingRec] = useState(null);

  const [trustSignalsData, setTrustSignalsData] = useState(null);
  const [isLoadingTrustSignals, setIsLoadingTrustSignals] = useState(false);
  const [trustSignalsError, setTrustSignalsError] = useState(null);
  const fetchedTrustSignalsForAuditIdRef = useRef(null);

  useEffect(() => {
    const checkAuthentication = () => {
      const token = localStorage.getItem("accessToken");
      const user = getUserFromToken(token);
      setIsAuthenticated(!!user);
    };

    checkAuthentication();

    // Listen for auth changes
    const handleAuthChange = () => checkAuthentication();
    window.addEventListener("authChange", handleAuthChange);

    return () => window.removeEventListener("authChange", handleAuthChange);
  }, []);

  // Detect navbar scroll state
  useEffect(() => {
    const handleScroll = () => {
      setNavScrolled(window.scrollY > 20);
    };

    handleScroll(); // Check initial state
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Link audit to user when they log in while viewing a report
  useEffect(() => {
    let isMounted = true;

    const linkAuditToUser = async () => {
      if (isAuthenticated && auditId) {
        try {
          const response = await api.post(`/audit/${auditId}/link`);
          if (isMounted && response.data?.status === "completed") {
            setFetchedAuditResponse(response.data);
          }
        } catch {
          // silently ignore link errors
        }
      }
    };

    linkAuditToUser();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, auditId]);

  // Fetch audit data from backend when loaded via URL (e.g., from sidebar)
  useEffect(() => {
    let isMounted = true;

    const fetchAuditData = async () => {
      // Avoid fetching if using prop or matching navigation state
      if (propAuditResponse || (navigationAuditResponse && navigationAuditId === urlAuditId)) {
        return;
      }

      if (urlAuditId) {
        setIsLoadingReport(true);
        // Clear previous data while fetching new report
        setFetchedAuditResponse(null);

        try {
          const response = await api.get(`/audit/${urlAuditId}`);
          if (!isMounted) return;

          const result = response.data;
          if (result?.status === "completed" && result?.data) {
            setFetchedAuditResponse(result);
          }
        } catch {
          // silently ignore fetch errors
        } finally {
          if (isMounted) {
            setIsLoadingReport(false);
          }
        }
      }
    };

    fetchAuditData();

    return () => {
      isMounted = false;
    };
  }, [urlAuditId, propAuditResponse, navigationAuditResponse, navigationAuditId]);

  // Update recommendations when data changes
  useEffect(() => {
    setRecommendations(buildRecommendationState(hasFullAuditAccess ? data?.suggestions || [] : []));
  }, [hasFullAuditAccess, data?.suggestions]);

  // Fetch the Trust Signals scorecard once per auditId, on first visit to that
  // tab, and keep it here (rather than in UpdatesView) so switching tabs away
  // and back reuses the cached result instead of refetching.
  useEffect(() => {
    if (activeTab !== "Trust Signals" || !auditId || !isAuthenticated || !hasFullAuditAccess) {
      return;
    }

    if (fetchedTrustSignalsForAuditIdRef.current === auditId) {
      return;
    }
    fetchedTrustSignalsForAuditIdRef.current = auditId;
    setTrustSignalsData(null);

    const fetchTrustSignals = async () => {
      setIsLoadingTrustSignals(true);
      setTrustSignalsError(null);

      try {
        const response = await api.get(`/audit/${auditId}/trust-signals`);
        setTrustSignalsData(response.data);
      } catch (err) {
        setTrustSignalsError(err.message || "Failed to analyze website. Please try again.");
      } finally {
        setIsLoadingTrustSignals(false);
      }
    };

    fetchTrustSignals();
  }, [activeTab, auditId, isAuthenticated, hasFullAuditAccess]);

  // Handle auto-scroll to top on tab change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeTab]);

  const tabs = [
    "Overview",
    "Trust Signals",
    "Detailed Analysis",
    "Recommendations",
    "Ai Guide",
    "Tailor as per your requirements",
  ];

  // Tabs that require login
  const protectedTabs = [
    "Detailed Analysis",
    "Recommendations",
    "Ai Guide",
    "Tailor as per your requirements",
    "Trust Signals",
  ];

  const handleToggleComplete = (id) => {
    setRecommendations((prev) =>
      prev.map((rec) => (rec.id === id ? { ...rec, completed: !rec.completed } : rec)),
    );
  };

  const completedCount = useMemo(
    () => recommendations.filter((r) => r.completed).length,
    [recommendations],
  );
  const recommendationsLength = recommendations.length;

  const quickInsights = {
    criticalPriority: recommendations.filter((r) => r.impact === "Critical").length,
    highPriority: recommendations.filter((r) => r.impact === "High").length,
    mediumPriority: recommendations.filter((r) => r.impact === "Medium").length,
    total: recommendations.length,
  };

  const topPriorities = recommendations.slice(0, 3).map((s) => ({
    text: s.description?.split(".")[0] + ".",
    description: s.description,
    impact: s.impact,
    funnelCategory: s.funnel_category,
  }));

  const openAuthInPlace = (mode) => {
    navigate(`${location.pathname}?auth=${mode}`);
  };

  // Render content or login prompt if tab is protected and user not logged in
  const renderReportContent = () => {
    if (protectedTabs.includes(activeTab) && (!isAuthenticated || !hasFullAuditAccess)) {
      return (
        <div className="text-center py-24">
          <Lock className="w-16 h-16 text-on-surface-variant mx-auto mb-6" />
          <div className="text-2xl font-bold text-on-surface-variant mb-4">Login Required</div>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Sign up or log in to access the full report experience.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => {
                openAuthInPlace("signup");
              }}
              className="block w-full max-w-xs mx-auto px-6 py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary rounded-xl hover:opacity-90 transition-colors font-medium"
            >
              Sign Up to Unlock
            </button>
            <button
              onClick={() => {
                openAuthInPlace("login");
              }}
              className="text-sm text-primary hover:opacity-80 transition-colors"
            >
              Already have an account? Log in
            </button>
          </div>
        </div>
      );
    }
    switch (activeTab) {
      case "Overview":
        return (
          <OverviewView
            data={data}
            technicalAnalysisData={technicalAnalysisData}
            completedCount={completedCount}
            quickInsights={quickInsights}
            topPriorities={topPriorities}
            recommendationsLength={recommendationsLength}
          />
        );
      case "Detailed Analysis":
        return <DetailedAnalysisView data={data} technicalAnalysisData={technicalAnalysisData} />;
      case "Recommendations":
        return (
          <RecommendationsView
            recommendations={recommendations}
            onToggleComplete={handleToggleComplete}
            onShowGuide={setViewingGuide}
            onStartAutoFix={setFixingRec}
          />
        );
      case "Ai Guide":
        return <AiOptimizationGuide />;
      case "Tailor as per your requirements":
        return <TailorRequirementsView />;
      case "Trust Signals":
        return (
          <UpdatesView
            auditData={trustSignalsData}
            isAnalyzing={isLoadingTrustSignals}
            error={trustSignalsError}
          />
        );
      default:
        return <OverviewView data={data} />;
    }
  };

  // Show loading state while fetching report data
  if (isLoadingReport) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading report..." />
      </div>
    );
  }

  // Show error if no data available
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-error text-xl mb-4">Report Not Found</div>
          <p className="text-on-surface-variant mb-6">
            {urlAuditId
              ? "This report could not be loaded. Please start a new audit to generate a report."
              : "No audit data available."}
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary rounded-xl hover:opacity-90 transition-colors"
          >
            Start New Audit
          </button>
        </div>
      </div>
    );
  }

  const handleConfirmAutoFix = (id) => {
    handleToggleComplete(id);
    setFixingRec(null);
  };

  // Matches the backend's normalize_hostname() so the Trends screen's website
  // picker resolves to the same group as this audit.
  const auditHostname = (() => {
    if (!data?.url) return "";
    try {
      return new URL(data.url).hostname.toLowerCase().replace(/^www\./, "");
    } catch {
      return data.url.toLowerCase().replace(/^www\./, "");
    }
  })();

  return (
    <div className="min-h-screen">
      <ImplementationGuideModal guide={viewingGuide} onClose={() => setViewingGuide(null)} />
      <AutoFixModal
        taskId={auditId}
        recommendation={fixingRec}
        onConfirmFix={handleConfirmAutoFix}
        onClose={() => setFixingRec(null)}
      />
      <header
        className="bg-surface-container-lowest border-b border-surface-container-high sticky z-20 transition-all duration-300"
        style={{
          top: navScrolled ? `${NAV_HEIGHT_SCROLLED}px` : `${NAV_HEIGHT_NORMAL}px`,
        }}
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-on-surface">LLM SEO Audit Results</h1>
              <p className="text-sm text-on-surface-variant hidden md:block">
                for:{" "}
                <a
                  href={data?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {data?.url}
                </a>
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isAuthenticated && (
                <button
                  onClick={() =>
                    navigate(
                      `/trends?type=${TREND_REPORT_TYPE.AUDIT}&group_key=${encodeURIComponent(auditHostname)}`,
                    )
                  }
                  title="View Trends"
                  aria-label="View Trends"
                  className="p-2 text-on-surface bg-surface-container-low border border-surface-container-high hover:bg-surface-container-high rounded-xl transition-colors flex items-center justify-center"
                >
                  <TrendingUp className="w-4 h-4" />
                </button>
              )}
              {isAuthenticated && hasFullAuditAccess && (
                <DownloadPdfButton reportType="audit" reportId={auditId} />
              )}
              <button
                onClick={() => {
                  if (onNewAudit) {
                    onNewAudit();
                  } else {
                    navigate("/");
                  }
                }}
                className="px-4 py-2 text-sm font-semibold text-on-primary bg-gradient-to-br from-primary to-primary-container rounded-xl hover:opacity-90 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" /> New Audit
              </button>
            </div>
          </div>
        </div>
      </header>
      <div
        className="bg-surface-container-lowest border-b border-surface-container-high sticky z-20 transition-all duration-300"
        style={{
          top: navScrolled ? `${NAV_HEIGHT_SCROLLED + 80}px` : `${NAV_HEIGHT_NORMAL + 80}px`,
        }}
      >
        <div className="container mx-auto px-6">
          <nav className="flex space-x-6">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 font-semibold text-sm transition-colors duration-200 border-b-2 ${
                  activeTab === tab
                    ? "border-primary text-primary"
                    : "border-transparent text-on-surface-variant hover:text-primary"
                }`}
              >
                {tab}
                {protectedTabs.includes(tab) && (!isAuthenticated || !hasFullAuditAccess) && (
                  <Lock className="w-3 h-3 opacity-50 ml-1 inline" />
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>
      <main
        className="container mx-auto p-6 pt-5 flex items-start justify-center transition-all duration-300"
        style={{
          minHeight: navScrolled
            ? `calc(100vh - ${NAV_HEIGHT_SCROLLED + 80 + 80}px)`
            : `calc(100vh - ${NAV_HEIGHT_NORMAL + 80 + 80}px)`,
        }}
      >
        <div className="w-full mt-8">
          {renderReportContent()}
          <CrossReportBanner currentReport="audit" />
        </div>
      </main>
    </div>
  );
};

export default Report;
